import { unwrapResult } from '@reduxjs/toolkit';
import { ExpertUpdatePasswordDto } from 'api/generated';
import classNames from 'classnames';
import { patientChangePasswordAsync } from 'features/patient/thunks';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { notify } from 'services/notificationService';
import { useAppDispatch } from 'store/reducers';
import { ButtonDefault, FormError, FormLabel, PasswordInput } from 'UIcomponents';
import { patterns } from 'utils/patterns';

import styles from '../../styles.module.scss';

export const ChangePasswordSection: FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'patient.patientSettingsTab' });

  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    formState,
    setError,
    getValues,
    reset,
    formState: { errors },
  } = useForm({ mode: 'onChange' });

  const onSubmit = (data: ExpertUpdatePasswordDto) => {
    dispatch(patientChangePasswordAsync(data))
      .then(unwrapResult)
      .then((res) => {
        notify('success', String(res));
        reset();
      })
      .catch((e) => {
        setError('currentPassword', { message: e.message });
      });
  };

  return (
    <>
      <h5 className={styles['password-section__title']}>{t('changePassword')}</h5>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={classNames(styles['password-section__inputs'])}>
          <div className={classNames(styles['password-section__input'])}>
            <div className={classNames('subhead_regular')}>
              <FormLabel text={t('labelCurrentPassword')} linkText={t('forgotPassword') as string} />
            </div>

            <Controller
              name="currentPassword"
              control={control}
              rules={{
                required: t('required') as string,
              }}
              render={({ field: { onChange, value } }) => (
                <PasswordInput
                  onChange={onChange}
                  value={value}
                  error={errors.currentPassword && 'error'}
                  customClassName={styles['password-section__custom-input']}
                />
              )}
            />

            {errors.currentPassword?.message && <FormError type="error" text={errors.currentPassword.message} />}
          </div>

          <div className={classNames(styles['password-section__input'])}>
            <div className={classNames('subhead_regular')}>
              <FormLabel text={t('newPassword')} />
            </div>

            <Controller
              name="newPassword"
              control={control}
              rules={{
                required: t('required') as string,
                pattern: {
                  value: patterns.password,
                  message: t('passwordPattern'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <PasswordInput
                  onChange={onChange}
                  value={value}
                  error={(errors.newPassword || errors.newPasswordConfirm?.type === 'matchesPreviousPassword') && true}
                  customClassName={styles['password-section__custom-input']}
                />
              )}
            />

            {errors.newPassword?.message && <FormError type="error" text={errors.newPassword.message} />}
          </div>

          <div className={classNames(styles['password-section__input'])}>
            <div className={classNames('subhead_regular')}>
              <FormLabel text={t('confirmedNewPassword')} />
            </div>

            <Controller
              name="confirmedNewPassword"
              control={control}
              rules={{
                required: t('required') as string,
                validate: {
                  matchesPreviousPassword: (value) => {
                    const { newPassword } = getValues();
                    return newPassword === value || (t('matchesPreviousPassword') as string);
                  },
                },
              }}
              render={({ field: { onChange, value } }) => (
                <PasswordInput
                  onChange={onChange}
                  value={value}
                  error={errors.confirmedNewPassword && true}
                  customClassName={styles['password-section__custom-input']}
                />
              )}
            />

            {errors.confirmedNewPassword?.message && (
              <FormError type="error" text={errors.confirmedNewPassword.message} />
            )}
          </div>
        </div>
        <div className={classNames('flexbox', 'justify-between', styles['password-section__button-container'])}>
          <ButtonDefault
            containerClassNames={styles['password-section__button']}
            type="submit"
            isDisabled={!formState.isValid}
            text={t('changePassword')}
          />
        </div>
      </form>
    </>
  );
};
